import { Show, type Component, type JSX } from 'solid-js';
import { Input } from '~ui/input';
import { Text } from '~ui/text';
import { parseNumber } from '~/lib/format';

// Input's base class carries `w-full`, and a `w-24` passed alongside it only
// wins or loses on stylesheet source order. So the width lives on this wrapper
// box instead and the input flexes to fill it — which is also what keeps the
// input off the label: the box is `shrink-0`, the label `min-w-0 truncate`, so
// a long label ellipsises on one line rather than wrapping under or behind it.
//
// The box is the full width regardless of prefix/suffix, so a column of these
// at one width lines its rules up into two continuous vertical seams down the
// pane. `bordered={false}` inside a table cell, where the <td> owns the rule so
// it runs the full cell height instead of just the control's.
const BORDER: Record<'x' | 'r' | 'l' | 'none', string> = { x: 'border-x', r: 'border-r', l: 'border-l', none: '' };

export const NumberBox: Component<{
    value: number;
    onValue: (n: number) => void;
    prefix?: string;
    suffix?: string;
    width?: string;
    /** Which rules the box draws. Nothing by default — only a segmented bar
        needs them, and there `r` lets each control share one rule with its
        neighbour instead of doubling up. */
    border?: 'x' | 'r' | 'l' | 'none';
    disabled?: boolean;
}> = (props) => (
    <div class={`flex h-full items-center whitespace-nowrap px-2 ${props.width ?? 'w-24 shrink-0'} ${BORDER[props.border ?? 'none']}`}>
        <Show when={props.prefix}>
            <Text tone='muted' class='shrink-0 pr-1'>
                {props.prefix}
            </Text>
        </Show>
        <div class='min-w-0 flex-1'>
            <Input
                bare
                type='number'
                class='text-right'
                value={Number.isFinite(props.value) ? Math.round(props.value * 1e4) / 1e4 : ''}
                disabled={props.disabled}
                onInput={(e) => {
                    const n = parseNumber(e.currentTarget.value);
                    if (n != null) props.onValue(n);
                }}
            />
        </div>
        <Show when={props.suffix}>
            <Text tone='muted' class='shrink-0 pl-1'>
                {props.suffix}
            </Text>
        </Show>
    </div>
);

/** A labelled row: label left, control right, one line, always. Stretches its
    children so a bordered control's rules span the whole row height. */
export const Row: Component<{ label: string; children: JSX.Element; hover?: boolean }> = (props) => (
    <div class={`flex h-row items-stretch justify-between gap-2 pl-2 ${props.hover === false ? '' : 'hover:bg-muted'}`}>
        <Text tone='muted' class='min-w-0 self-center truncate'>
            {props.label}
        </Text>
        {props.children}
    </div>
);

export const NumberField: Component<{
    label: string;
    value: number;
    onValue: (n: number) => void;
    prefix?: string;
    suffix?: string;
    width?: string;
}> = (props) => (
    <Row label={props.label}>
        <NumberBox value={props.value} onValue={props.onValue} prefix={props.prefix} suffix={props.suffix} width={props.width} />
    </Row>
);

export const StatRow: Component<{ label: string; value: JSX.Element; tone?: 'green' | 'red' | 'muted' }> = (props) => (
    <Row label={props.label} hover={false}>
        {/* Matches NumberBox's box width and padding so the numbers in a stat
            block line up with the numbers in the editable fields above them. */}
        <div class='flex w-24 shrink-0 items-center justify-end self-center px-2'>
            <Text tone={props.tone} weight='medium' class='truncate whitespace-nowrap'>
                {props.value}
            </Text>
        </div>
    </Row>
);
